/**
 * FileMakerFunctionsBridge.ts - fm_* functions for FileMaker integration
 * 
 * Provides utility functions callable from FileMaker using the registerFunctions() pattern.
 * Uses OpCentral packages directly (Translatr, Fylr) without unnecessary wrapper layers.
 * 
 * CHANGE HISTORY:
 * - 251108_1242 [pending] danielbeer: Refactored to use registerFunctions() pattern
 * - 251101_1450 [pending] danielbeer: Initial creation
 */

import { getFileMakerBridge } from './FileMakerBridge';
import type { SettingsConfig, SystemStatus } from './FileMakerTypes';
import { Fylr } from '@opcentral/fylr';
import { 
  xmlToJson, 
  jsonToXml, 
  csvToJson, 
  jsonToCsv,
  listXlsxSheets, 
  xlsxToJson, 
  docxToHtml, 
  autoParse 
} from '@opcentral/translatr';

/**
 * Global settings
 */
let settings: SettingsConfig = {
  pdfInteractive: true,
  pdfInitialScale: 1.25,
  pdfBlockSelection: false,
  extractTextMaxPages: 10,
  cacheExpiry: 86400000,
  encryptCache: true,
  maxCacheSize: 100,
  autoUpdate: true,
  updateCheckInterval: 3600000,
  offlineMode: false,
  environment: 'auto',
  performanceMode: 'balanced',
  validateIntegrity: true,
  logErrors: true,
  reportErrors: false
};

/**
 * FileMaker Functions Bridge
 * Registers all fm_* functions callable from FileMaker
 */
export class FileMakerFunctionsBridge {
  private fm = getFileMakerBridge();

  /**
   * Register all fm_* functions with FileMaker
   */
  registerFunctions(): void {
    this.fm.registerFunctions({
      // ============================================================================
      // Data Transformation Functions (using Translatr directly)
      // ============================================================================
      
      fm_xmlToJson: (xmlString: string): string => {
        try {
          const result = xmlToJson(xmlString);
          return JSON.stringify(result);
        } catch (e) {
          return JSON.stringify({ error: (e as Error).message });
        }
      },

      fm_jsonToXml: (jsonString: string, rootName: string = 'root'): string => {
        try {
          const json = JSON.parse(jsonString);
          return jsonToXml(json, rootName);
        } catch (e) {
          return `<error>${(e as Error).message}</error>`;
        }
      },

      fm_csvToJson: (csv: string): string => {
        try {
          const result = csvToJson(csv);
          return JSON.stringify(result);
        } catch (e) {
          return JSON.stringify({ error: (e as Error).message });
        }
      },

      fm_jsonToCsv: (jsonString: string): string => {
        try {
          const json = JSON.parse(jsonString);
          return jsonToCsv(json);
        } catch (e) {
          return `error: ${(e as Error).message}`;
        }
      },

      // ============================================================================
      // Document Extraction (using Fylr + Translatr)
      // ============================================================================

      fm_extract: async (filename: string, mime: string, base64: string, optionsJson?: string): Promise<string> => {
        try {
          const buffer = Buffer.from(base64, 'base64');
          
          // Use Translatr to extract content based on file type
          let content;
          if (mime.includes('word') || mime.includes('docx')) {
            content = await docxToHtml(buffer);
          } else if (mime.includes('sheet') || mime.includes('xlsx')) {
            content = await xlsxToJson(buffer);
          } else {
            // Try auto-parse for other formats
            content = await autoParse(buffer);
          }

          return JSON.stringify({
            success: true,
            filename,
            mime,
            content,
            size: buffer.length,
            extracted: new Date().toISOString()
          });
        } catch (e) {
          return JSON.stringify({ 
            success: false, 
            error: (e as Error).message,
            stack: (e as Error).stack 
          });
        }
      },

      fm_listSheets: async (filename: string, mime: string, base64: string): Promise<string> => {
        try {
          const buffer = Buffer.from(base64, 'base64');
          const sheets = await listXlsxSheets(buffer);

          return JSON.stringify({
            success: true,
            filename,
            mime,
            sheets,
            count: sheets.length
          });
        } catch (e) {
          return JSON.stringify({ 
            success: false, 
            error: (e as Error).message,
            stack: (e as Error).stack
          });
        }
      },

      // ============================================================================
      // Settings Management
      // ============================================================================

      fm_getSettings: (): string => {
        return JSON.stringify(settings);
      },

      fm_updateSettings: (settingsJson: string): string => {
        try {
          const newSettings = JSON.parse(settingsJson);
          settings = { ...settings, ...newSettings };
          return JSON.stringify({ success: true, settings });
        } catch (e) {
          return JSON.stringify({ success: false, error: (e as Error).message });
        }
      },

      // ============================================================================
      // System Status
      // ============================================================================

      fm_getSystemStatus: (): string => {
        const status: SystemStatus = {
          version: '4.0.0',
          ready: true,
          environment: this.fm.getEnvironment(),
          timestamp: Date.now(),
          settings
        };
        return JSON.stringify(status);
      }
    });
  }

  /**
   * Get current settings
   */
  getSettings(): SettingsConfig {
    return { ...settings };
  }

  /**
   * Update settings
   */
  updateSettings(newSettings: Partial<SettingsConfig>): void {
    settings = { ...settings, ...newSettings };
  }
}

/**
 * Singleton instance
 */
let instance: FileMakerFunctionsBridge | null = null;

/**
 * Get FileMaker Functions Bridge instance
 */
export function getFileMakerFunctionsBridge(): FileMakerFunctionsBridge {
  if (!instance) {
    instance = new FileMakerFunctionsBridge();
  }
  return instance;
}
